import styles from "./layout.module.css";
import { Menu, MenuItem, Dialog, Snackbar, Alert, Autocomplete, TextField, InputAdornment } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../components/Loader";
import { authData } from "./GetAuth";
import dayjs from "dayjs";
import { Calendar, CreditCard, Lock, Menu as MenuIcon, Search, Settings, User } from "react-feather";
import { StorageRemoveItem, STORAGE_KEYS } from "./StorageComp";
import UpdateProfileForm from "./popups/UpdateProfileForm";
import { formatGBP2, useBillingUpdates } from "./Features";

const STATUS_LABEL = { trial: "Free trial", active: "Basic plan", suspended: "Suspended", cancelled: "Subscription ended" };

// Super admin: jump to any company by name, domain or email
const CompanySearch = ({ apiToken }) => {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      axios.get(process.env.REACT_APP_API_URL + "v1/admin/company-options", {
        params: { search: input },
        headers: { Authorization: `Bearer ${apiToken}` },
      }).then((response) => {
        if (response.data.status === "SUCCESS") setOptions(response.data.data);
      }).catch(() => { });
    }, 250);
    return () => clearTimeout(timer.current);
  }, [input, apiToken]);

  return (<Autocomplete
    className={`${styles.HeadCompanySearch}`}
    size="small"
    options={options}
    filterOptions={(x) => x}
    getOptionLabel={(o) => o?.company_name || ""}
    renderOption={(props, o) => (<li {...props} key={o.id}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{o.company_name}</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{o.domain || "No domain"} · {o.billing_status}</div>
      </div>
    </li>)}
    onInputChange={(e, value) => setInput(value)}
    onChange={(e, value) => { if (value) navigate(`/super-admin/companies/${value.id}`); }}
    renderInput={(params) => (<TextField
      {...params}
      placeholder="Search companies"
      InputProps={{ ...params.InputProps, startAdornment: (<InputAdornment position="start"><Search size={16} /></InputAdornment>) }}
    />)}
    blurOnSelect
    clearOnBlur
  />);
}

const Header = ({ onMenuToggle }) => {
  useBillingUpdates();
  const auth = authData();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [formOpenUpdateProfile, setFormOpenUpdateProfile] = useState(false);
  const userProfileData = localStorage.getItem("authData");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [loading, setLoading] = useState(false);
  const isSuper = auth?.user_type === 1;
  const billing = auth?.subscription;

  const goMyProfile = () => {
    navigate("/my-profile");
  }

  const goCompanyProfile = () => {
    navigate("/company-profile");
  }

  const Logout = () => {
    StorageRemoveItem(STORAGE_KEYS.AUTH);
    navigate("/sign-in");
  }

  return (<header className={`${styles.HeaderMain}`}>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.HeaderMainUnder}`}>
      <div className={`${styles.HeaderRow}`}>
        <button
          type="button"
          className={`${styles.MenuToggle}`}
          aria-label="Toggle navigation menu"
          onClick={onMenuToggle}
        >
          <MenuIcon />
        </button>

        {isSuper && <div className={`${styles.HeadPlatformSec}`}>
          <span className={`${styles.HeadPlatformChip}`}>Platform admin</span>
          <CompanySearch apiToken={auth?.api_token} />
        </div>}

        <div className={`${styles.HeadRightSec}`}>
          <button className={`${styles.ProfileBU}`} onClick={(e) => setAnchorEl(e.currentTarget)}><User /></button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={(e) => setAnchorEl(null)}
            onClick={(e) => setAnchorEl(null)}
          >
            <div className={`${styles.DropdownProfileSec}`}>
              <div className={`${styles.ProfileAvatar}`}><p>{auth?.name.substring(0, 1)}</p></div>
              <p className={`${styles.ProfileText}`}>{auth?.name}</p>
              <p className={`${styles.ProfileText}`}>{auth?.email}</p>
              <p className={`${styles.ProfileText}`}>{isSuper ? "Platform administrator" : auth?.company?.company_name}</p>
            </div>
            {!isSuper && billing && <div className={`${styles.DropdownPlanSec}`}>
              <p className={`${styles.DropdownPlanText}`}><CreditCard />{STATUS_LABEL[billing.status] || "Basic plan"}{billing.monthly_estimate !== undefined ? ` · ${formatGBP2(billing.monthly_estimate)}/mo` : ""}</p>
              <p className={`${styles.DropdownPlanText}`}><Calendar />
                {billing.cancels_at
                  ? `Subscription ends ${dayjs(billing.cancels_at).format("DD MMM YYYY")}`
                  : billing.status === "trial" && billing.trial_ends_at
                  ? `Trial ends ${dayjs(billing.trial_ends_at).format("DD MMM YYYY")}`
                  : billing.next_billing_date ? `Next bill ${dayjs(billing.next_billing_date).format("DD MMM YYYY")}` : "No upcoming bill"}
              </p>
              {auth?.user_type === 2 && <button onClick={() => navigate("/company-profile?tab=addons")}>Manage add-ons</button>}
            </div>}
            <MenuItem onClick={goMyProfile} className={`${styles.DropdownProfileLink}`}><Settings />Account Setting</MenuItem>
            {auth?.user_type === 2 && <MenuItem onClick={goCompanyProfile} className={`${styles.DropdownProfileLink}`}><Settings />Company Setting</MenuItem>}
            <MenuItem onClick={Logout} className={`${styles.DropdownProfileLink}`}><Lock />Sign Out</MenuItem>
          </Menu>
        </div>
      </div>
    </div>

    <Dialog open={formOpenUpdateProfile} onClose={(e) => setFormOpenUpdateProfile(false)}>
      <UpdateProfileForm setFormOpenUpdateProfile={setFormOpenUpdateProfile.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} userProfileData={userProfileData} />
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert onClose={(e) => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>{snackbarMessage}</Alert>
    </Snackbar>
  </header>);
};

export default Header;
